import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { UserPledgesListProvider } from "CrossFeatureComponents/UserPledgesList/UserPledgesListContext";
import { RecurringGiftsProvider } from "CrossFeatureComponents/UserRecurringGifts/RecurringGiftsContext";
import {
  ManageMyGivingPageProvider,
  useManageMyGivingPage,
} from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";

import { getBackgroundGradientFromColorScheme } from "Utilities/Colors/colorUtils";

import ManageMyGivingPageColors from "./ManageMyGivingPageColors";
import ManageMyGivingPageContent from "./ManageMyGivingPageContent";
import { useFetchLandingPageData } from "./useFetchLandingPageData";
import { useFetchUserPledgeAndGivingData } from "./useFetchUserPledgeAndGivingData";
import useRefetchMMGData from "./useRefetchMMGData";

function ManageMyGivingPage() {
  const [readyToRender, setReadyToRender] = useState(false);
  const { state, setState } = useManageMyGivingPage();
  const [hasDoneInitialFetch, setHasDoneInitialFetch] = useState(false);

  const {
    userID,
    network,
    pageSettings,
    givingLandingPageEnabled,
    recurringGiftUpdateComplete,
  } = state;

  const refetchMMGData = useRefetchMMGData();
  const fetchLandingPageData = useFetchLandingPageData();

  useEffect(
    function handleInitialPageDataFetch() {
      if (!hasDoneInitialFetch) {
        fetchLandingPageData();
        setHasDoneInitialFetch(true);
      }
    },
    [hasDoneInitialFetch, fetchLandingPageData]
  );

  useEffect(
    function setPageTitle() {
      if (network.name) document.title = `${network.name} // Giving`;
    },
    [network.name]
  );

  useEffect(
    function enablePageForRenderingOr404() {
      if (givingLandingPageEnabled) {
        setReadyToRender(true);
      } else if (givingLandingPageEnabled === false) {
        window.location = "https://flocknote.com/need-help-404/";
      }
    },
    [givingLandingPageEnabled]
  );

  const fetchUserPledgeAndGivingData = useFetchUserPledgeAndGivingData();

  useEffect(
    function loadUsersPledgeAndGivingData() {
      if (userID) {
        fetchUserPledgeAndGivingData();
      }
    },
    [userID, fetchUserPledgeAndGivingData]
  );

  const background = getBackgroundGradientFromColorScheme(
    pageSettings?.colorScheme
  );

  useEffect(
    function refetchMMGDataOnRecurringGiftUpdateSave() {
      // This is updated in CrossFeatureComponents/UserRecurringGifts/EditRecurringGiftModal/EditPanel/SaveButton.js
      // See ManageMyGivingPageWithProviders where the update function is passed to the recurring gifts provider
      // I hate this pattern, but I couldn't come up with another way to do it.
      if (recurringGiftUpdateComplete) {
        refetchMMGData();
        setState({ recurringGiftUpdateComplete: false });
      }
    },
    [recurringGiftUpdateComplete, refetchMMGData, setState]
  );

  return (
    <Wrapper
      data-cy="ManageMyGivingPage_Container"
      style={{ justifyContent: "flex-start", gap: 0 }}
    >
      {readyToRender ? (
        <>
          <BackgroundGradient background={background} />

          <SafeArea>
            <ManageMyGivingPageColors>
              <ManageMyGivingPageContent />
            </ManageMyGivingPageColors>
          </SafeArea>
        </>
      ) : null}
    </Wrapper>
  );
}

function ManageMyGivingPageWithProviders() {
  const { setState } = useManageMyGivingPage();
  // When recurringGiftUpdateComplete is true, MMG data is refetched in ManageMyGivingPage above
  const recurringGiftUpdateSuccessAction = () => {
    setState({ recurringGiftUpdateComplete: true });
  };

  return (
    <RecurringGiftsProvider onSuccessfulSave={recurringGiftUpdateSuccessAction}>
      <UserPledgesListProvider>
        <ManageMyGivingPage />
      </UserPledgesListProvider>
    </RecurringGiftsProvider>
  );
}

export default function ManageMyGivingPageProviderWithContent() {
  return (
    <ManageMyGivingPageProvider>
      <ManageMyGivingPageWithProviders />
    </ManageMyGivingPageProvider>
  );
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const SafeArea = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
`;
const BackgroundGradient = styled.div`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: ${({ background }) => background};
  box-shadow: rgba(0, 0, 0, 0.2) 0 0 24px;
`;
